import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPlatform, isNativePlatform, triggerHaptic } from '../utils/capacitor';

const DEFAULT_THRESHOLD = 72;
const DEFAULT_MAX_PULL = 120;
const REFRESH_HOLD_HEIGHT = 56;
const PULL_TRANSITION = 'height 180ms ease';

const SCROLLABLE_OVERFLOW_VALUES = ['auto', 'scroll', 'overlay'];

export function getPullDistance(deltaY, maxPull = DEFAULT_MAX_PULL) {
  if (deltaY <= 0) {
    return 0;
  }

  const dampedDistance = deltaY * 0.45;
  return Math.min(dampedDistance, maxPull);
}

export function hasScrollableParentBetween(target, container) {
  if (!target || !container || !(target instanceof HTMLElement) || !(container instanceof HTMLElement)) {
    return false;
  }

  let current = target;

  while (current && current !== container && current !== document.body) {
    const style = window.getComputedStyle(current);
    const overflowY = style?.overflowY;
    const isScrollable = SCROLLABLE_OVERFLOW_VALUES.includes(overflowY) && current.scrollHeight > current.clientHeight;

    if (isScrollable) {
      return true;
    }

    current = current.parentElement;
  }

  return false;
}

function getPageScrollTop() {
  return (
    window.pageYOffset ||
    document.scrollingElement?.scrollTop ||
    document.documentElement?.scrollTop ||
    document.body?.scrollTop ||
    0
  );
}

function isSupportedNativePlatform() {
  if (!isNativePlatform()) {
    return false;
  }

  const platform = getPlatform();
  return platform === 'ios' || platform === 'android';
}

export default function useNativePullToRefresh({
  onRefresh,
  disabled = false,
  threshold = DEFAULT_THRESHOLD,
  maxPull = DEFAULT_MAX_PULL,
}) {
  const refreshHandlerRef = useRef(onRefresh);
  const disabledRef = useRef(disabled);
  const thresholdRef = useRef(threshold);
  const maxPullRef = useRef(maxPull);
  const gestureRef = useRef({
    startY: 0,
    eligible: false,
  });
  // Live gesture state is mirrored into refs so the touch handlers always read
  // the latest value without needing to re-bind. Listeners are bound ONCE, when
  // the container element actually mounts (see the callback ref below).
  const pullDistanceRef = useRef(0);
  const isRefreshingRef = useRef(false);
  // The DOM node the listeners are currently bound to, and the matching detach
  // function. Tracked so the callback ref can rebind if the node changes.
  const boundNodeRef = useRef(null);
  const detachRef = useRef(null);

  const [pullDistance, setPullDistanceState] = useState(0);
  const [isRefreshing, setIsRefreshingState] = useState(false);

  const isEnabled = useMemo(() => isSupportedNativePlatform(), []);

  refreshHandlerRef.current = onRefresh;
  disabledRef.current = disabled;
  thresholdRef.current = threshold;
  maxPullRef.current = maxPull;

  const setPullDistance = useCallback((value) => {
    pullDistanceRef.current = value;
    setPullDistanceState(value);
  }, []);

  const setIsRefreshing = useCallback((value) => {
    isRefreshingRef.current = value;
    setIsRefreshingState(value);
  }, []);

  // Bind the touch listeners to a specific node. Returns a detach function.
  // Handlers read all live values through refs, so this runs exactly once per
  // mounted node — no re-subscription mid-gesture.
  const bindListeners = useCallback((container) => {
    const resetGesture = () => {
      gestureRef.current = {
        startY: 0,
        eligible: false,
      };
    };

    const handleTouchStart = (event) => {
      if (disabledRef.current || isRefreshingRef.current || getPageScrollTop() > 0) {
        resetGesture();
        return;
      }

      const touch = event.touches?.[0];
      if (!touch) {
        resetGesture();
        return;
      }

      if (hasScrollableParentBetween(event.target, container)) {
        resetGesture();
        return;
      }

      gestureRef.current = {
        startY: touch.clientY,
        eligible: true,
      };
    };

    const handleTouchMove = (event) => {
      if (!gestureRef.current.eligible || disabledRef.current || isRefreshingRef.current) {
        return;
      }

      if (getPageScrollTop() > 0) {
        setPullDistance(0);
        resetGesture();
        return;
      }

      const touch = event.touches?.[0];
      if (!touch) {
        return;
      }

      const nextPullDistance = getPullDistance(touch.clientY - gestureRef.current.startY, maxPullRef.current);

      if (nextPullDistance <= 0) {
        setPullDistance(0);
        return;
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      setPullDistance(nextPullDistance);
    };

    const handleTouchEnd = async () => {
      const shouldRefresh =
        gestureRef.current.eligible &&
        pullDistanceRef.current >= thresholdRef.current &&
        !disabledRef.current &&
        !isRefreshingRef.current;
      resetGesture();

      if (!shouldRefresh) {
        setPullDistance(0);
        return;
      }

      setIsRefreshing(true);
      setPullDistance(REFRESH_HOLD_HEIGHT);

      try {
        await triggerHaptic('light');
        await refreshHandlerRef.current?.();
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [setPullDistance, setIsRefreshing]);

  // Callback ref — React invokes this with the node when the container mounts
  // and with null when it unmounts. Binding here (instead of in an effect) is
  // what makes pull-to-refresh work when the container is rendered AFTER an
  // initial loading screen: a plain effect runs once with a null ref and never
  // re-binds, so the listeners would never attach.
  const containerRef = useCallback((node) => {
    if (node === boundNodeRef.current) {
      return;
    }

    if (detachRef.current) {
      detachRef.current();
      detachRef.current = null;
    }

    boundNodeRef.current = node;

    if (node && isEnabled) {
      detachRef.current = bindListeners(node);
    }
  }, [isEnabled, bindListeners]);

  useEffect(() => {
    if (disabled && !isRefreshingRef.current) {
      setPullDistance(0);
    }
  }, [disabled, setPullDistance]);

  return {
    containerRef,
    isEnabled,
    isRefreshing,
    isPulling: pullDistance > 0 && !isRefreshing,
    progress: Math.min(pullDistance / threshold, 1),
    indicatorHeight: isRefreshing ? REFRESH_HOLD_HEIGHT : pullDistance,
    indicatorTransition: isRefreshing ? PULL_TRANSITION : 'none',
  };
}
